import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Box,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Typography,
    IconButton,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Tooltip,
    Stack,
    CardContent,
    Avatar,
    useTheme,
    alpha,
    Fade,
    Badge,
    LinearProgress,
    Skeleton,
    Slide,
    Zoom,
    Collapse,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Slider,
    Switch,
    FormControlLabel
} from '@mui/material';
import { 
    Block, 
    CheckCircle, 
    Edit, 
    ReceiptLong, 
    ShoppingCart, 
    Paid, 
    Receipt, 
    AccessTime, 
    ContentCopy, 
    Link as LinkIcon, 
    LocationCity, 
    Map, 
    Inventory2, 
    Scale,
    Search,
    FilterList,
    TrendingUp,
    People,
    Message,
    SmartToy,
    Refresh,
    Download,
    ExpandMore,
    DateRange,
    AccountBalance,
    Visibility,
    VisibilityOff,
    Clear,
    PersonAdd,
    Analytics,
    Timeline,
    CalendarToday,
    AccountBalanceWallet
} from '@mui/icons-material';
import CommentIcon from '@mui/icons-material/ChatBubbleOutline';
import ReportIcon from '@mui/icons-material/ReportGmailerrorred';
import { Notes } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import telegramUserService from '../services/telegramUserService';
import { orderService } from '../services/orderService';
import axios from '../utils/axios';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import UserNoteDialog from '../components/UserNoteDialog';
import UserBalanceHistoryDialog from '../components/UserBalanceHistoryDialog';

export default function TelegramUsers() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [balanceAmount, setBalanceAmount] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [openNoteDialog, setOpenNoteDialog] = useState(false);
    const [selectedUserForNote, setSelectedUserForNote] = useState(null);
    const [openBalanceHistoryDialog, setOpenBalanceHistoryDialog] = useState(false);
    const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
    const [balanceRange, setBalanceRange] = useState([0, 100000]);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [showInactiveUsers, setShowInactiveUsers] = useState(true);

    const currency = useMemo(() => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }), []);
    const numberFmt = useMemo(() => new Intl.NumberFormat('ru-RU'), []);

    // Диалог заказов пользователя
    const [ordersOpen, setOrdersOpen] = useState(false);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [ordersStats, setOrdersStats] = useState(null);
    const [ordersStatusFilter, setOrdersStatusFilter] = useState('');
    // Детали заказа
    const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    // Snackbar helpers and clipboard utils
    const { enqueueSnackbar } = useSnackbar();
    const truncateOrderId = (id) => {
        if (!id) return '-';
        const s = String(id);
        if (s.length <= 14) return s;
        return `${s.slice(0, 8)}…${s.slice(-6)}`;
    };
    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar('Скопировано', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Не удалось скопировать', { variant: 'error' });
        }
    };

    // Загрузка всех пользователей
    const loadData = async () => {
        try {
            setLoading(true);
            const usersData = await telegramUserService.getAllUsers();
            setUsers(usersData);
            applyAllFilters(usersData, searchQuery, statusFilter, balanceRange, dateRange, showInactiveUsers);
        } catch (error) {
            console.error('Error loading users:', error);
            enqueueSnackbar('Ошибка загрузки данных', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Открыть диалог заказов пользователя
    const handleOpenUserOrders = async (user) => {
        setSelectedUser(user);
        setOrdersOpen(true);
        await fetchUserOrders(user.userId, ordersStatusFilter);
    };

    const fetchUserOrders = async (userId, status = '') => {
        try {
            setOrdersLoading(true);
            const { orders, stats } = await telegramUserService.getUserOrders(userId, status ? { status } : {});
            setOrders(orders);
            setOrdersStats(stats);
        } catch (e) {
            console.error('Error loading user orders:', e);
        } finally {
            setOrdersLoading(false);
        }
    };

    const handleChangeOrdersStatus = async (e) => {
        const status = e.target.value;
        setOrdersStatusFilter(status);
        if (selectedUser) {
            await fetchUserOrders(selectedUser.userId, status);
        }
    };

    // Ненаход: диалог и обработчик
    const [nfOpen, setNfOpen] = useState(false);
    const [nfAmount, setNfAmount] = useState('');
    const [nfComment, setNfComment] = useState('');
    const [nfLoading, setNfLoading] = useState(false);
    const [nfOrder, setNfOrder] = useState(null);
    const [commentOpen, setCommentOpen] = useState(false);
    const [commentText, setCommentText] = useState('');

    const openNotFoundDialog = (order) => {
        setNfOrder(order);
        setNfAmount('');
        setNfComment('');
        setNfOpen(true);
    };

    const handleMarkNotFound = async () => {
        if (!nfOrder) return;
        try {
            setNfLoading(true);
            await orderService.markNotFound(nfOrder.order_id, {
                topUpAmount: nfAmount ? Number(nfAmount) : 0,
                comment: nfComment
            });
            setNfOpen(false);
            // Перезагрузим заказы и статистику
            if (selectedUser) {
                await fetchUserOrders(selectedUser.userId, ordersStatusFilter);
            }
        } catch (e) {
            console.error('Failed to mark not found:', e);
        } finally {
            setNfLoading(false);
        }
    };

    // Поиск и фильтрация пользователей
    const handleSearch = (event) => {
        const query = event.target.value.toLowerCase();
        setSearchQuery(query);
        applyAllFilters(users, query, statusFilter, balanceRange, dateRange, showInactiveUsers);
    };

    const handleStatusFilter = (event) => {
        const status = event.target.value;
        setStatusFilter(status);
        applyAllFilters(users, searchQuery, status, balanceRange, dateRange, showInactiveUsers);
    };

    const handleBalanceRangeChange = (event, newValue) => {
        setBalanceRange(newValue);
        applyAllFilters(users, searchQuery, statusFilter, newValue, dateRange, showInactiveUsers);
    };

    const handleDateRangeChange = (field, value) => {
        const newDateRange = { ...dateRange, [field]: value };
        setDateRange(newDateRange);
        applyAllFilters(users, searchQuery, statusFilter, balanceRange, newDateRange, showInactiveUsers);
    };

    const handleInactiveUsersToggle = (event) => {
        const show = event.target.checked;
        setShowInactiveUsers(show);
        applyAllFilters(users, searchQuery, statusFilter, balanceRange, dateRange, show);
    };

    const clearAllFilters = () => {
        setSearchQuery('');
        setStatusFilter('all');
        setBalanceRange([0, 100000]);
        setDateRange({ start: '', end: '' });
        setShowInactiveUsers(true);
        applyAllFilters(users, '', 'all', [0, 100000], { start: '', end: '' }, true);
    };

    const applyAllFilters = (usersData, query, status, balRange, dateRng, showInactive) => {
        let filtered = usersData.filter(user => {
            // Text search
            const matchesSearch = !query || 
                user.firstName?.toLowerCase().includes(query) ||
                user.lastName?.toLowerCase().includes(query) ||
                user.username?.toLowerCase().includes(query) ||
                user.userId.toString().includes(query);

            // Status filter
            const matchesStatus = status === 'all' || 
                (status === 'blocked' && user.isBlocked) ||
                (status === 'active' && !user.isBlocked);

            // Balance range filter
            const userBalance = user.balance || 0;
            const matchesBalance = userBalance >= balRange[0] && userBalance <= balRange[1];

            // Date range filter (last activity)
            let matchesDate = true;
            if (dateRng.start || dateRng.end) {
                const userDate = new Date(user.lastActivity);
                if (dateRng.start) {
                    matchesDate = matchesDate && userDate >= new Date(dateRng.start);
                }
                if (dateRng.end) {
                    matchesDate = matchesDate && userDate <= new Date(dateRng.end + 'T23:59:59');
                }
            }

            // Inactive users filter (last 7 days)
            const isRecentlyActive = new Date(user.lastActivity) > new Date(Date.now() - 7*24*60*60*1000);
            const matchesActivity = showInactive || isRecentlyActive;

            return matchesSearch && matchesStatus && matchesBalance && matchesDate && matchesActivity;
        });

        setFilteredUsers(filtered);
        setPage(0); // Сбросить на первую страницу при фильтрации
    };

    // Export functionality
    const exportToExcel = async () => {
        try {
            const XLSX = await import('xlsx');
            const workbook = XLSX.utils.book_new();
            
            const exportData = filteredUsers.map(user => ({
                'ID': user.userId,
                'Имя': `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                'Username': user.username || '',
                'Баланс': user.balance || 0,
                'Сообщений': user.stats?.totalMessages || 0,
                'Команд': user.stats?.commandsUsed || 0,
                'Успешных заказов': user.ordersSummary?.successPaid || 0,
                'Ненаходов': user.ordersSummary?.notFound || 0,
                'Оплаченных': user.ordersSummary?.paidCount || 0,
                'Неоплаченных': user.ordersSummary?.unpaidCount || 0,
                'Статус': user.isBlocked ? 'Заблокирован' : 'Активен',
                'Последняя активность': new Date(user.lastActivity).toLocaleString('ru-RU'),
                'Активированные боты': (user.activatedBots || []).map(bot => `@${bot.username || bot}`).join(', ')
            }));
            
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            
            // Auto-size columns
            const colWidths = Object.keys(exportData[0] || {}).map(key => ({
                wch: Math.max(key.length, 15)
            }));
            worksheet['!cols'] = colWidths;
            
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Пользователи');
            
            const fileName = `telegram_users_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(workbook, fileName);
            
            enqueueSnackbar('Данные экспортированы в Excel', { variant: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            enqueueSnackbar('Ошибка экспорта данных', { variant: 'error' });
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (users.length > 0) {
            applyAllFilters(users, searchQuery, statusFilter, balanceRange, dateRange, showInactiveUsers);
        }
    }, [users, searchQuery, statusFilter, balanceRange, dateRange, showInactiveUsers]);

    // Блокировка/разблокировка пользователя
    const handleToggleBlock = async (userId) => {
        try {
            await telegramUserService.toggleBlock(userId);
            loadData(); // Перезагрузка данных
        } catch (error) {
            console.error('Error toggling block:', error);
        }
    };

    // Открытие диалога заметок
    const handleOpenNoteDialog = (user) => {
        setSelectedUserForNote(user);
        setOpenNoteDialog(true);
    };

    // Закрытие диалога заметок
    const handleCloseNoteDialog = () => {
        setOpenNoteDialog(false);
        setSelectedUserForNote(null);
    };

    // Изменение баланса
    const handleUpdateBalance = async () => {
        if (!selectedUser) return;

        try {
            const amount = parseFloat(balanceAmount);
            if (isNaN(amount)) {
                console.error('Invalid amount');
                return;
            }

            await telegramUserService.updateBalance(selectedUser.userId, amount);
            setOpenDialog(false);
            setBalanceAmount('');
            loadData(); // Перезагрузка данных
        } catch (error) {
            console.error('Error updating balance:', error);
        }
    };

    const theme = useTheme();

    return (
        <Fade in={true} timeout={800}>
        <Container maxWidth="xl" sx={{ pb: 4 }}>
            {/* Hero Section */}
            <Paper 
                elevation={4}
                sx={{ 
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    p: 3,
                    mb: 3,
                    color: 'text.primary'
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main', mr: 2, width: 56, height: 56 }}>
                            <SmartToy sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" fontWeight="bold">
                                Telegram Пользователи
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Управление пользователями и их активностью
                            </Typography>
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Экспорт в Excel">
                            <IconButton 
                                onClick={exportToExcel}
                                disabled={filteredUsers.length === 0 || loading}
                                sx={{ 
                                    color: 'text.primary',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                                    '&:disabled': { opacity: 0.5 }
                                }}
                            >
                                <Download />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Обновить данные">
                            <IconButton 
                                onClick={loadData}
                                disabled={loading}
                                sx={{ 
                                    color: 'text.primary',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                                    '&:disabled': { opacity: 0.5 }
                                }}
                            >
                                {loading ? <CircularProgress size={20} sx={{ color: 'text.primary' }} /> : <Refresh />}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </Paper>
            {/* Поиск и фильтры */}
            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Search color="primary" />
                            Поиск и фильтры
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button 
                                size="small" 
                                onClick={clearAllFilters}
                                startIcon={<Clear />}
                                disabled={loading}
                            >
                                Сбросить
                            </Button>
                            <Button 
                                size="small" 
                                onClick={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}
                                endIcon={<ExpandMore sx={{ transform: advancedFiltersOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />}
                                disabled={loading}
                            >
                                Расширенные
                            </Button>
                        </Box>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Поиск пользователей"
                                value={searchQuery}
                                onChange={handleSearch}
                                placeholder="Введите имя, username, ID"
                                disabled={loading}
                                InputProps={{
                                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '&:hover fieldset': {
                                            borderColor: theme.palette.primary.main,
                                        }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Статус пользователя</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={handleStatusFilter}
                                    label="Статус пользователя"
                                    disabled={loading}
                                    startAdornment={<FilterList sx={{ mr: 1, color: 'text.secondary' }} />}
                                    sx={{
                                        borderRadius: 2,
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.primary.main,
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Все пользователи</MenuItem>
                                    <MenuItem value="active">Только активные</MenuItem>
                                    <MenuItem value="blocked">Только заблокированные</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
                
                {/* Расширенные фильтры */}
                <Collapse in={advancedFiltersOpen}>
                    <Divider />
                    <Box sx={{ p: 3, pt: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountBalance fontSize="small" />
                                    Диапазон баланса: {currency.format(balanceRange[0])} - {currency.format(balanceRange[1])}
                                </Typography>
                                <Slider
                                    value={balanceRange}
                                    onChange={handleBalanceRangeChange}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={100000}
                                    step={1000}
                                    disabled={loading}
                                    valueLabelFormat={(value) => currency.format(value)}
                                    sx={{ mt: 1 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DateRange fontSize="small" />
                                    Период активности
                                </Typography>
                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        type="date"
                                        label="С"
                                        value={dateRange.start}
                                        onChange={(e) => handleDateRangeChange('start', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                        disabled={loading}
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        type="date"
                                        label="По"
                                        value={dateRange.end}
                                        onChange={(e) => handleDateRangeChange('end', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                        disabled={loading}
                                        sx={{ flex: 1 }}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showInactiveUsers}
                                            onChange={handleInactiveUsersToggle}
                                            disabled={loading}
                                        />
                                    }
                                    label="Показывать неактивных пользователей (более 7 дней)"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            </Paper>
            {/* Статистика */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {loading ? (
                    // Skeleton loading for statistics
                    Array.from({ length: 4 }).map((_, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                                    <Skeleton variant="text" width={100} height={24} />
                                </Box>
                                <Skeleton variant="text" width={80} height={48} sx={{ mb: 1 }} />
                                <Skeleton variant="text" width={150} height={20} />
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <>
                        <Grid item xs={12} sm={6} md={3}>
                    <Card 
                        elevation={3}
                        sx={{ 
                            p: 3, 
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }}
                    >
                        <Box display="flex" alignItems="center" mb={2}>
                            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                                <People />
                            </Avatar>
                            <Typography variant="h6" color="primary" fontWeight="bold">
                                Пользователи
                            </Typography>
                        </Box>
                        <Typography variant="h3" fontWeight="bold" color="primary">
                            {filteredUsers.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            из {users.length} • Активных за 24ч: {filteredUsers.filter(u => new Date(u.lastActivity) > new Date(Date.now() - 24*60*60*1000)).length}
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card 
                        elevation={3}
                        sx={{ 
                            p: 3, 
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.15)}`
                            }
                        }}
                    >
                        <Box display="flex" alignItems="center" mb={2}>
                            <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                                <Message />
                            </Avatar>
                            <Typography variant="h6" color="success.main" fontWeight="bold">
                                Активность
                            </Typography>
                        </Box>
                        <Typography variant="h3" fontWeight="bold" color="success.main">
                            {numberFmt.format(filteredUsers.reduce((sum, u) => sum + u.stats.totalMessages, 0))}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            сообщений • {numberFmt.format(filteredUsers.reduce((sum, u) => sum + u.stats.commandsUsed, 0))} команд
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card 
                        elevation={3}
                        sx={{ 
                            p: 3, 
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 8px 25px ${alpha(theme.palette.warning.main, 0.15)}`
                            }
                        }}
                    >
                        <Box display="flex" alignItems="center" mb={2}>
                            <Avatar sx={{ bgcolor: theme.palette.warning.main, mr: 2 }}>
                                <TrendingUp />
                            </Avatar>
                            <Typography variant="h6" color="warning.main" fontWeight="bold">
                                Баланс
                            </Typography>
                        </Box>
                        <Typography variant="h3" fontWeight="bold" color="warning.main">
                            {currency.format(filteredUsers.reduce((sum, u) => sum + u.balance, 0))}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            общий баланс пользователей
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card 
                        elevation={3}
                        sx={{ 
                            p: 3, 
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 8px 25px ${alpha(theme.palette.error.main, 0.15)}`
                            }
                        }}
                    >
                        <Box display="flex" alignItems="center" mb={2}>
                            <Avatar sx={{ bgcolor: theme.palette.error.main, mr: 2 }}>
                                <Block />
                            </Avatar>
                            <Typography variant="h6" color="error.main" fontWeight="bold">
                                Заблокировано
                            </Typography>
                        </Box>
                        <Typography variant="h3" fontWeight="bold" color="error.main">
                            {filteredUsers.filter(u => u.isBlocked).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            пользователей заблокировано
                        </Typography>
                    </Card>
                        </Grid>
                    </>
                )}
            </Grid>

            {/* Таблица пользователей */}
            <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">
                        📋 Список пользователей ({loading ? '...' : filteredUsers.length})
                    </Typography>
                    {!loading && filteredUsers.length === 0 && users.length > 0 && (
                        <Chip 
                            icon={<FilterList />} 
                            label="Нет результатов" 
                            color="warning" 
                            variant="outlined" 
                        />
                    )}
                </Box>
                {loading && <LinearProgress />}
            <TableContainer>
                <Table sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Пользователь</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Баланс</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Активность</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Заказы</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Боты</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Последний раз</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Статус</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Действия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            // Skeleton loading for table rows
                            Array.from({ length: rowsPerPage }).map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                                            <Box>
                                                <Skeleton variant="text" width={120} height={20} />
                                                <Skeleton variant="text" width={80} height={16} />
                                                <Skeleton variant="text" width={60} height={16} />
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell><Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                                    <TableCell>
                                        <Skeleton variant="text" width={100} height={20} />
                                        <Skeleton variant="text" width={80} height={16} />
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5}>
                                            <Stack direction="row" spacing={1}>
                                                <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
                                                <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
                                            </Stack>
                                            <Stack direction="row" spacing={1}>
                                                <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
                                                <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
                                            </Stack>
                                        </Stack>
                                    </TableCell>
                                    <TableCell><Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: 1 }} /></TableCell>
                                    <TableCell><Skeleton variant="text" width={100} height={20} /></TableCell>
                                    <TableCell><Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Skeleton variant="circular" width={32} height={32} />
                                            <Skeleton variant="circular" width={32} height={32} />
                                            <Skeleton variant="circular" width={32} height={32} />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : filteredUsers.length === 0 ? (
                            // Empty state
                            <TableRow>
                                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 8 }}>
                                    <Stack alignItems="center" spacing={2}>
                                        <Avatar sx={{ width: 64, height: 64, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                            <People sx={{ fontSize: 32, color: 'primary.main' }} />
                                        </Avatar>
                                        <Typography variant="h6" color="text.secondary">
                                            {users.length === 0 ? 'Нет пользователей' : 'Пользователи не найдены'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {users.length === 0 
                                                ? 'Пользователи появятся здесь после их регистрации в боте'
                                                : 'Попробуйте изменить параметры поиска или фильтры'
                                            }
                                        </Typography>
                                        {users.length > 0 && (
                                            <Button 
                                                variant="outlined" 
                                                onClick={clearAllFilters}
                                                startIcon={<Clear />}
                                            >
                                                Сбросить фильтры
                                            </Button>
                                        )}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ) : (
                            (rowsPerPage > 0
                            ? filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            : filteredUsers
                        ).map((user, index) => (
                            <TableRow 
                                key={user.userId}
                                sx={{
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                                        transform: 'scale(1.001)',
                                        transition: 'all 0.2s ease'
                                    },
                                    '&:nth-of-type(even)': {
                                        bgcolor: alpha(theme.palette.grey[100], 0.3)
                                    }
                                }}
                            >
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <Avatar 
                                            sx={{ 
                                                mr: 2, 
                                                bgcolor: user.isBlocked ? theme.palette.error.main : theme.palette.primary.main,
                                                width: 40,
                                                height: 40
                                            }}
                                        >
                                            {(user.firstName || 'U').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                @{user.username || 'нет username'}
                                            </Typography>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                ID: {user.userId}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={currency.format(user.balance || 0)}
                                        color={user.balance > 0 ? 'success' : 'default'}
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Stack spacing={0}>
                                        <Typography variant="body2">Сообщений: {numberFmt.format(user.stats.totalMessages || 0)}</Typography>
                                        <Typography variant="caption" color="text.secondary">Команд: {numberFmt.format(user.stats.commandsUsed || 0)}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Stack spacing={0.5}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip size="small" color="success" variant="outlined" label={`Усп: ${user.ordersSummary?.successPaid || 0}`} />
                                            <Chip size="small" color="error" variant="outlined" label={`Нн: ${user.ordersSummary?.notFound || 0}`} />
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip size="small" color="primary" variant="outlined" label={`Опл: ${user.ordersSummary?.paidCount || 0}`} />
                                            <Chip size="small" variant="outlined" label={`Неопл: ${user.ordersSummary?.unpaidCount || 0}`} />
                                        </Stack>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {(user.activatedBots || []).map((bot, idx) => (
                                            <Tooltip key={(bot._id || bot) + '_' + idx} title={`Бот @${bot.username || bot}`}>
                                                <Chip label={`@${bot.username || bot}`}
                                                      size="small"
                                                      color="info"
                                                      variant="outlined" />
                                            </Tooltip>
                                        ))}
                                        {(!user.activatedBots || user.activatedBots.length === 0) && (
                                            <Chip label="Нет" size="small" variant="outlined" />
                                        )}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    {formatDistanceToNow(new Date(user.lastActivity), {
                                        addSuffix: true,
                                        locale: ru
                                    })}
                                </TableCell>
                                <TableCell>
                                    <Badge 
                                        badgeContent={user.isBlocked ? '!' : null}
                                        color="error"
                                    >
                                        <Chip 
                                            label={user.isBlocked ? 'Заблокирован' : 'Активен'}
                                            color={user.isBlocked ? 'error' : 'success'}
                                            variant={user.isBlocked ? 'filled' : 'outlined'}
                                            size="small"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1}>
                                        <Tooltip title={user.isBlocked ? 'Разблокировать' : 'Заблокировать'}>
                                            <IconButton
                                                size="small"
                                                color={user.isBlocked ? 'success' : 'error'}
                                                onClick={() => handleToggleBlock(user.userId)}
                                                sx={{
                                                    bgcolor: alpha(user.isBlocked ? theme.palette.success.main : theme.palette.error.main, 0.1),
                                                    '&:hover': {
                                                        bgcolor: alpha(user.isBlocked ? theme.palette.success.main : theme.palette.error.main, 0.2),
                                                        transform: 'scale(1.1)'
                                                    }
                                                }}
                                            >
                                                {user.isBlocked ? <CheckCircle /> : <Block />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Изменить баланс">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => {
                                                    setSelectedUser(user);
                                                    setOpenDialog(true);
                                                }}
                                                sx={{
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                                                        transform: 'scale(1.1)'
                                                    }
                                                }}
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Заказы пользователя">
                                            <IconButton
                                                size="small"
                                                color="info"
                                                onClick={() => handleOpenUserOrders(user)}
                                                sx={{
                                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.info.main, 0.2),
                                                        transform: 'scale(1.1)'
                                                    }
                                                }}
                                            >
                                                <ReceiptLong />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Заметка">
                                            <IconButton
                                                size="small"
                                                color="secondary"
                                                onClick={() => handleOpenNoteDialog(user)}
                                                sx={{
                                                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.secondary.main, 0.2),
                                                        transform: 'scale(1.1)'
                                                    }
                                                }}
                                            >
                                                <Notes />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="История баланса">
                                            <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={() => {
                                                    setSelectedUserForHistory(user);
                                                    setOpenBalanceHistoryDialog(true);
                                                }}
                                                sx={{
                                                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.warning.main, 0.2),
                                                        transform: 'scale(1.1)'
                                                    }
                                                }}
                                            >
                                                <AccountBalanceWallet />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredUsers.length}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="Строк на странице"
                    labelDisplayedRows={({ from, to, count }) => 
                        `${from}-${to} из ${count}`
                    }
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.grey[50], 0.5)
                    }}
                />
            </Paper>

            {/* Диалог изменения баланса */}
            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Slide}
                TransitionProps={{ direction: 'up' }}
            >
                <DialogTitle sx={{ 
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <Edit />
                    Изменить баланс пользователя
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {selectedUser && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                            <Typography variant="subtitle2" color="primary">
                                {selectedUser.firstName} {selectedUser.lastName} (@{selectedUser.username || 'нет'})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Текущий баланс: {currency.format(selectedUser.balance || 0)}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        autoFocus
                        label="Сумма изменения"
                        type="number"
                        fullWidth
                        value={balanceAmount}
                        onChange={(e) => setBalanceAmount(e.target.value)}
                        helperText="Положительное число для пополнения, отрицательное для списания"
                        InputProps={{
                            startAdornment: <AccountBalance sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setOpenDialog(false)} sx={{ borderRadius: 2 }}>
                        Отмена
                    </Button>
                    <Button 
                        onClick={handleUpdateBalance} 
                        variant="contained" 
                        startIcon={<Edit />}
                        sx={{ 
                            borderRadius: 2,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': { bgcolor: 'primary.dark' }
                        }}
                    >
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Диалог просмотра комментария ненахода */}
            <Dialog open={commentOpen} onClose={() => setCommentOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Комментарий ненахода</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {commentText || '—'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCommentOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>
            {/* Диалог "Ненаход" */}
            <Dialog open={nfOpen} onClose={() => setNfOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Пометить как ненаход</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Заказ {nfOrder?.order_id}. Вы можете опционально пополнить баланс пользователя.
                    </Typography>
                    <TextField
                        label="Сумма пополнения"
                        type="number"
                        fullWidth
                        value={nfAmount}
                        onChange={(e) => setNfAmount(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        label="Комментарий"
                        fullWidth
                        multiline
                        minRows={2}
                        value={nfComment}
                        onChange={(e) => setNfComment(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNfOpen(false)}>Отмена</Button>
                    <Button variant="contained" color="warning" onClick={handleMarkNotFound} disabled={nfLoading} startIcon={<ReportIcon />}>
                        Ненаход
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог заказов пользователя */}
            <Dialog 
                open={ordersOpen} 
                onClose={() => setOrdersOpen(false)} 
                maxWidth="lg" 
                fullWidth
                TransitionComponent={Zoom}
            >
                <DialogTitle sx={{ 
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 3
                }}>
                    <Avatar sx={{ 
                        width: 48, 
                        height: 48,
                        bgcolor: 'action.selected', color: 'primary.main',
                        fontSize: '1.5rem'
                    }}>
                        {(selectedUser?.firstName || 'U').slice(0,1)}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Заказы пользователя
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {selectedUser ? `${selectedUser.firstName || ''} ${selectedUser.lastName || ''} (@${selectedUser.username || 'нет'})` : ''}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Статус заказа</InputLabel>
                            <Select label="Статус заказа" value={ordersStatusFilter} onChange={handleChangeOrdersStatus}>
                                <MenuItem value=""><em>Все</em></MenuItem>
                                <MenuItem value="paid">Оплачен</MenuItem>
                                <MenuItem value="pending">В ожидании</MenuItem>
                                <MenuItem value="cancelled">Отменен</MenuItem>
                                <MenuItem value="wait_confirm">Ожид. подтверждения</MenuItem>
                                <MenuItem value="waitcheck">Ожид. проверки</MenuItem>
                                <MenuItem value="checkload">Проверка загрузки</MenuItem>
                                <MenuItem value="appelation">Аппеляция</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {ordersLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {ordersStats && (
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card sx={{ bgcolor: 'background.paper' }}>
                                            <CardContent>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Avatar sx={{ bgcolor: 'primary.main' }}><ShoppingCart fontSize="small" /></Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">Оплаченных заказов</Typography>
                                                        <Typography variant="h6">{numberFmt.format((ordersStats.byStatus?.paid || 0) || ((ordersStats.successPaid || 0) + (ordersStats.notFound || 0)))}</Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card>
                                            <CardContent>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Avatar sx={{ bgcolor: 'success.main' }}><Paid fontSize="small" /></Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">Оплачено (base)</Typography>
                                                        <Typography variant="h6">{currency.format(ordersStats.sumPaid || 0)}</Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card>
                                            <CardContent>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Avatar sx={{ bgcolor: 'info.main' }}><Receipt fontSize="small" /></Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">Сумма заказов (base)</Typography>
                                                        <Typography variant="h6">{currency.format(ordersStats.sumTotal || 0)}</Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card>
                                            <CardContent>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Avatar sx={{ bgcolor: 'warning.main' }}><AccessTime fontSize="small" /></Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">Последний заказ</Typography>
                                                        <Typography variant="body2">{ordersStats.lastOrderAt ? new Date(ordersStats.lastOrderAt).toLocaleString('ru-RU') : '-'}</Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack direction="row" spacing={2}>
                                            <Chip label={`Успешных: ${ordersStats.successPaid || 0}`} color="success" variant="outlined" />
                                            <Chip label={`Ненаходов: ${ordersStats.notFound || 0}`} color="error" variant="outlined" />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            )}

                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>№ заказа</TableCell>
                                            <TableCell>Дата</TableCell>
                                            <TableCell align="right">Действия</TableCell>
                                            <TableCell>Тип</TableCell>
                                            <TableCell>Статус</TableCell>
                                            <TableCell>Сумма (base)</TableCell>
                                            <TableCell>Город</TableCell>
                                            <TableCell>Район</TableCell>
                                            <TableCell>Товар</TableCell>
                                            <TableCell>Фасовка</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orders.map((o) => {
                                            const statusColor = o.status === 'paid' ? 'success' : (o.status === 'cancelled' ? 'error' : 'warning');
                                            return (
                                                <TableRow key={o.order_id} hover>
                                                    <TableCell>
                                                        <Tooltip title="Нажмите, чтобы скопировать">
                                                            <Button size="small" onClick={() => copyToClipboard(o.order_id)} sx={{ fontFamily: 'monospace', textTransform: 'none', p: 0, minWidth: 0 }}>
                                                                {truncateOrderId(o.order_id)}
                                                            </Button>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>{new Date(o.created_at).toLocaleString('ru-RU')}</TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                            <Button size="small" onClick={() => { setSelectedOrder(o); setOrderDetailsOpen(true); }}>Подробнее</Button>
                                                            {o.status === 'paid' && !o.not_found && (
                                                                <Button size="small" color="warning" startIcon={<ReportIcon />} onClick={() => openNotFoundDialog(o)}>
                                                                    Ненаход
                                                                </Button>
                                                            )}
                                                            {o.not_found && (
                                                                <>
                                                                    <Chip label="НЕНАХОД" color="error" size="small" />
                                                                    {o.not_found_comment && (
                                                                        <Tooltip title="Комментарий ненахода">
                                                                            <IconButton size="small" onClick={() => { setCommentText(o.not_found_comment); setCommentOpen(true); }}>
                                                                                <CommentIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip size="small" label={o.type} variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip size="small" label={o.status} color={statusColor} variant={o.status === 'paid' ? 'filled' : 'outlined'} />
                                                    </TableCell>
                                                    <TableCell>{currency.format(o.base_price || 0)}</TableCell>
                                                    <TableCell>{o.address_id?.city?.name || '-'}</TableCell>
                                                    <TableCell>{o.address_id?.district?.name || '-'}</TableCell>
                                                    <TableCell>{o.address_id?.product?.name || '-'}</TableCell>
                                                    <TableCell>{o.address_id?.packaging?.name || '-'}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button 
                        onClick={() => setOrdersOpen(false)}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                    >
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог деталей заказа */}
            <Dialog 
                open={orderDetailsOpen} 
                onClose={() => { setOrderDetailsOpen(false); }} 
                maxWidth="lg" 
                fullWidth
                TransitionComponent={Fade}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>#</Avatar>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Заказ</Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="h6" sx={{ mr: 1, fontFamily: 'monospace' }}>{selectedOrder?.order_id}</Typography>
                                    {selectedOrder && (
                                        <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={() => navigator.clipboard.writeText(selectedOrder.order_id)}>Копировать</Button>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                        {selectedOrder && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                {selectedOrder.bot_id?.username && (
                                    <Chip size="small" color="info" label={`@${selectedOrder.bot_id.username}`} />
                                )}
                                <Chip size="small" label={selectedOrder.type} variant="outlined" />
                                <Chip size="small" label={selectedOrder.status} color={selectedOrder.status === 'paid' ? 'success' : (selectedOrder.status === 'cancelled' ? 'error' : 'warning')} />
                                <Typography variant="body2" color="text.secondary">{new Date(selectedOrder.created_at).toLocaleString('ru-RU')}</Typography>
                            </Stack>
                        )}
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2, maxHeight: '70vh', overflowY: 'auto' }}>
                    {selectedOrder && (
                        <Stack spacing={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">Суммы</Typography>
                                            <Stack spacing={0.75} mt={1}>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2">Базовая сумма</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{currency.format(selectedOrder.base_price || 0)}</Typography>
                                                </Stack>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2">Оплата балансом</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{currency.format(selectedOrder.cupon_price || 0)}</Typography>
                                                </Stack>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2">Итого (с комиссией)</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{currency.format(selectedOrder.total_price || 0)}</Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Receipt fontSize="inherit" />
                                                    <Typography variant="caption" color="text.secondary">Коэффициент комиссии: {selectedOrder.service_fee ?? 1.3}x</Typography>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">Платеж</Typography>
                                            <Stack spacing={0.75} mt={1}>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2">Метод</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{selectedOrder.payment_method || '-'}</Typography>
                                                </Stack>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2">Транзакция</Typography>
                                                    <Typography variant="body2" fontFamily="monospace">{selectedOrder.payment_details?.transaction_id || '-'}</Typography>
                                                </Stack>
                                                {selectedOrder.payment_details?.payment_link && (
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography variant="body2">Ссылка</Typography>
                                                        <Button startIcon={<LinkIcon fontSize="small" />} href={selectedOrder.payment_details.payment_link} target="_blank" rel="noreferrer" size="small">Открыть</Button>
                                                    </Stack>
                                                )}
                                                {selectedOrder.payment_details?.payment_time && (
                                                    <Stack direction="row" justifyContent="space-between">
                                                        <Typography variant="body2">Время оплаты</Typography>
                                                        <Typography variant="body2">{new Date(selectedOrder.payment_details.payment_time).toLocaleString('ru-RU')}</Typography>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">Адрес</Typography>
                                    <Grid container spacing={2} mt={0.5}>
                                        <Grid item xs={12} sm={6}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <LocationCity fontSize="small" />
                                                <Typography variant="body2">Город: <b>{selectedOrder.address_id?.city?.name || '-'}</b></Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Map fontSize="small" />
                                                <Typography variant="body2">Район: <b>{selectedOrder.address_id?.district?.name || '-'}</b></Typography>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Inventory2 fontSize="small" />
                                                <Typography variant="body2">Товар: <b>{selectedOrder.address_id?.product?.name || '-'}</b></Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Scale fontSize="small" />
                                                <Typography variant="body2">Фасовка: <b>{selectedOrder.address_id?.packaging?.name || '-'}</b></Typography>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>Координаты/Фото:</Typography>
                                            <Paper variant="outlined" sx={{ p: 1, bgcolor: 'background.default' }}>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mr: 1 }}>{selectedOrder.address_id?.photo || '-'}</Typography>
                                                    {!!selectedOrder.address_id?.photo && (
                                                        <>
                                                            <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={() => navigator.clipboard.writeText(selectedOrder.address_id.photo)}>Копировать</Button>
                                                            {/^https?:\/\//i.test(selectedOrder.address_id.photo) && (
                                                                <Button size="small" startIcon={<LinkIcon fontSize="small" />} href={selectedOrder.address_id.photo} target="_blank" rel="noreferrer">Открыть</Button>
                                                            )}
                                                            {/^\s*-?\d+\.\d+\s*,\s*-?\d+\.\d+\s*$/i.test(selectedOrder.address_id.photo) && (
                                                                <Button size="small" href={`https://maps.google.com/?q=${encodeURIComponent(selectedOrder.address_id.photo)}`} target="_blank" rel="noreferrer">На карте</Button>
                                                            )}
                                                        </>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button 
                        onClick={() => setOrderDetailsOpen(false)}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                    >
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог заметок пользователя */}
            <UserNoteDialog
                open={openNoteDialog}
                onClose={handleCloseNoteDialog}
                user={selectedUserForNote}
            />

            {/* Диалог истории баланса */}
            <UserBalanceHistoryDialog
                open={openBalanceHistoryDialog}
                onClose={() => setOpenBalanceHistoryDialog(false)}
                user={selectedUserForHistory}
            />
        </Container>
        </Fade>
    );
}
